/**
 * Cycle payment derivation + full cycle model (docs/04 §1–§3).
 * Task 10: assignCycle, projected generation, boundary windows.
 */

import type { Cents } from './money';
import type { CardPaymentInput } from './state';
import {
  addDays,
  businessDate,
  compareBusinessDates,
  daysInMonth,
  diffDays,
  isInInclusiveRange,
  onDayOfMonth,
  type BusinessDate,
} from './time';

export type StatementCycleInput = {
  id: string;
  card_id: string;
  deleted_at: string | null;
  cycle_start_date: string;
  cycle_end_date: string;
  statement_date: string | null;
  payment_due_date: string | null;
  is_projected: boolean;
  statement_balance_cents: Cents | null;
  /** Present on real ingested cycles; null when unknown / not on minimal grace fixtures. */
  minimum_payment_cents: Cents | null;
  source_id: string | null;
  autopay_scheduled_cents: Cents | null;
  autopay_scheduled_date: string | null;
  status: string;
  paid_in_full_override: boolean | null;
};

export type PaymentAllocationInput = {
  id: string;
  card_id: string;
  deleted_at: string | null;
  payment_id: string;
  statement_cycle_id: string | null;
  transaction_id: string | null;
  amount_cents: Cents;
};

export type PaymentHistoryCoverageInput = {
  card_id: string;
  deleted_at: string | null;
  covers_from: string;
  covers_through: string;
  is_complete: boolean;
};

const CLOSED_STATUSES = new Set([
  'closed',
  'statement_issued',
  'payment_due',
  'paid',
  'past_due',
  'reconciled',
]);

export function isClosedCycleStatus(status: string): boolean {
  return CLOSED_STATUSES.has(status);
}

/**
 * Mirrors `cycle_payment_derivation.paid_in_full_by_due_date`.
 * Returns true / false / null (unknown). Never invents false from missing payments alone.
 */
export function derivedPaidInFullByDueDate(
  cycle: StatementCycleInput,
  payments: readonly CardPaymentInput[],
  allocations: readonly PaymentAllocationInput[],
  coverage: readonly PaymentHistoryCoverageInput[],
): boolean | null {
  if (cycle.paid_in_full_override !== null) {
    return cycle.paid_in_full_override;
  }
  if (cycle.statement_balance_cents === null || cycle.payment_due_date === null) {
    return null;
  }
  if (cycle.statement_balance_cents <= 0n) {
    return true;
  }

  const due = businessDate(cycle.payment_due_date);
  let paidCents = 0n;
  for (const al of allocations) {
    if (al.deleted_at !== null) continue;
    if (al.statement_cycle_id !== cycle.id) continue;
    const pay = payments.find((p) => p.id === al.payment_id && p.deleted_at === null);
    if (!pay || pay.status !== 'posted' || pay.posted_date === null) continue;
    if (compareBusinessDates(businessDate(pay.posted_date), due) > 0) continue;
    paidCents += al.amount_cents;
  }

  if (paidCents >= cycle.statement_balance_cents) {
    return true;
  }

  const historyComplete = coverage.some(
    (c) =>
      c.card_id === cycle.card_id &&
      c.deleted_at === null &&
      c.is_complete &&
      compareBusinessDates(businessDate(c.covers_from), businessDate(cycle.cycle_end_date)) <= 0 &&
      compareBusinessDates(businessDate(c.covers_through), due) >= 0,
  );

  if (historyComplete) return false;
  return null;
}

export function closedCyclesSortedDesc(
  cardId: string,
  cycles: readonly StatementCycleInput[],
): StatementCycleInput[] {
  return cycles
    .filter((c) => c.card_id === cardId && c.deleted_at === null && isClosedCycleStatus(c.status))
    .slice()
    .sort((a, b) => {
      const byEnd = compareBusinessDates(
        businessDate(b.cycle_end_date),
        businessDate(a.cycle_end_date),
      );
      if (byEnd !== 0) return byEnd;
      return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
    });
}

/** Newest closed cycle ended more than 70 days before asOf → stale history. */
export function statementHistoryStale(
  newestClosed: StatementCycleInput,
  asOf: BusinessDate,
): boolean {
  const end = businessDate(newestClosed.cycle_end_date);
  return compareBusinessDates(asOf, addDays(end, 70)) > 0;
}

// ---------------------------------------------------------------------------
// Task 10 — assignCycle + projected generation (docs/04 §1)
// ---------------------------------------------------------------------------

export type CycleAnchors = {
  /** 1–31; month-length-clamped when applied (docs/01 statement_day_of_month). */
  statement_day_of_month: number;
  due_days_after_close: number;
};

export type CycleAssignment = 'confirmed' | 'estimated' | 'unassigned';

export type ProjectionConfidence = 'high' | 'low';

export type AssignCycleResult = {
  statement_cycle_id: string | null;
  assignment: CycleAssignment;
  /** Newly generated projected cycle — caller persists; null when none created. */
  generated_cycle: StatementCycleInput | null;
  /** Null when no projection was involved (confirmed match or unassigned). */
  projection_confidence: ProjectionConfidence | null;
};

function yearOf(d: BusinessDate): number {
  return Number(d.slice(0, 4));
}

function monthOf(d: BusinessDate): number {
  return Number(d.slice(5, 7));
}

function dayOf(d: BusinessDate): number {
  return Number(d.slice(8, 10));
}

/**
 * Infer the stored statement_day_of_month from a real cycle end.
 * Last-day-of-month closes map to 31 (month-length clamp on apply).
 */
function inferredStatementDay(cycleEnd: BusinessDate): number {
  const y = yearOf(cycleEnd);
  const m = monthOf(cycleEnd);
  const d = dayOf(cycleEnd);
  if (d === daysInMonth(y, m)) return 31;
  return d;
}

function liveCardCycles(
  cardId: string,
  cycles: readonly StatementCycleInput[],
): StatementCycleInput[] {
  return cycles.filter((c) => c.card_id === cardId && c.deleted_at === null);
}

function realStatementsSortedDesc(
  cardId: string,
  cycles: readonly StatementCycleInput[],
): StatementCycleInput[] {
  return liveCardCycles(cardId, cycles)
    .filter((c) => !c.is_projected && c.payment_due_date !== null)
    .slice()
    .sort((a, b) => {
      const byEnd = compareBusinessDates(
        businessDate(b.cycle_end_date),
        businessDate(a.cycle_end_date),
      );
      if (byEnd !== 0) return byEnd;
      return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
    });
}

/**
 * Inclusive window containing `postedDate` for a statement_day_of_month anchor.
 * Never invents weekend/holiday shifts — only month-length clamping (docs/04 §1).
 */
export function projectedCycleWindow(
  postedDate: BusinessDate,
  statementDayOfMonth: number,
): { cycle_start_date: BusinessDate; cycle_end_date: BusinessDate } {
  const y = yearOf(postedDate);
  const m = monthOf(postedDate);
  let end = onDayOfMonth(y, m, statementDayOfMonth);
  if (compareBusinessDates(postedDate, end) > 0) {
    const nextY = m === 12 ? y + 1 : y;
    const nextM = m === 12 ? 1 : m + 1;
    end = onDayOfMonth(nextY, nextM, statementDayOfMonth);
  }
  const endY = yearOf(end);
  const endM = monthOf(end);
  const prevY = endM === 1 ? endY - 1 : endY;
  const prevM = endM === 1 ? 12 : endM - 1;
  const prevEnd = onDayOfMonth(prevY, prevM, statementDayOfMonth);
  return { cycle_start_date: addDays(prevEnd, 1), cycle_end_date: end };
}

/**
 * Learn anchors from real statements. Never invents defaults.
 * High confidence only when the last two agree on day + due_days.
 */
export function learnCycleAnchors(
  cardId: string,
  cycles: readonly StatementCycleInput[],
): { anchors: CycleAnchors; confidence: ProjectionConfidence } | null {
  const real = realStatementsSortedDesc(cardId, cycles);
  if (real.length === 0) return null;

  const newest = real[0];
  if (newest === undefined || newest.payment_due_date === null) return null;
  const newestEnd = businessDate(newest.cycle_end_date);
  const newestDue = businessDate(newest.payment_due_date);
  const newestAnchors: CycleAnchors = {
    statement_day_of_month: inferredStatementDay(newestEnd),
    due_days_after_close: diffDays(newestEnd, newestDue),
  };

  if (real.length === 1) {
    return { anchors: newestAnchors, confidence: 'low' };
  }

  const prior = real[1];
  if (prior === undefined || prior.payment_due_date === null) {
    return { anchors: newestAnchors, confidence: 'low' };
  }
  const priorEnd = businessDate(prior.cycle_end_date);
  const priorDue = businessDate(prior.payment_due_date);
  const priorDay = inferredStatementDay(priorEnd);
  const priorDueDays = diffDays(priorEnd, priorDue);

  if (
    priorDay === newestAnchors.statement_day_of_month &&
    priorDueDays === newestAnchors.due_days_after_close
  ) {
    return { anchors: newestAnchors, confidence: 'high' };
  }
  // Disagree → do not average; keep newest, flag low confidence.
  return { anchors: newestAnchors, confidence: 'low' };
}

/**
 * Card fields win when both are set (evidence-backed). Otherwise learn from cycles.
 * Never invents a missing half of the pair.
 */
export function resolveCycleAnchors(input: {
  card_id: string;
  card_statement_day_of_month: number | null;
  card_due_days_after_close: number | null;
  cycles: readonly StatementCycleInput[];
}): { anchors: CycleAnchors; confidence: ProjectionConfidence } | null {
  const day = input.card_statement_day_of_month;
  const dueDays = input.card_due_days_after_close;
  if (day !== null && dueDays !== null) {
    const realCount = realStatementsSortedDesc(input.card_id, input.cycles).length;
    return {
      anchors: { statement_day_of_month: day, due_days_after_close: dueDays },
      confidence: realCount >= 2 ? 'high' : 'low',
    };
  }
  return learnCycleAnchors(input.card_id, input.cycles);
}

function findContainingCycle(
  cardId: string,
  posted: BusinessDate,
  cycles: readonly StatementCycleInput[],
): StatementCycleInput | null {
  const matches = liveCardCycles(cardId, cycles).filter((c) =>
    isInInclusiveRange(posted, businessDate(c.cycle_start_date), businessDate(c.cycle_end_date)),
  );
  if (matches.length === 0) return null;
  const real = matches.find((c) => !c.is_projected);
  return real ?? matches[0] ?? null;
}

function buildProjectedCycle(args: {
  id: string;
  cardId: string;
  anchors: CycleAnchors;
  posted: BusinessDate;
}): StatementCycleInput {
  const window = projectedCycleWindow(args.posted, args.anchors.statement_day_of_month);
  const due = addDays(window.cycle_end_date, args.anchors.due_days_after_close);
  return {
    id: args.id,
    card_id: args.cardId,
    deleted_at: null,
    cycle_start_date: window.cycle_start_date,
    cycle_end_date: window.cycle_end_date,
    statement_date: window.cycle_end_date,
    payment_due_date: due,
    is_projected: true,
    statement_balance_cents: null,
    minimum_payment_cents: null,
    source_id: null,
    autopay_scheduled_cents: null,
    autopay_scheduled_date: null,
    status: 'projected',
    paid_in_full_override: null,
  };
}

/**
 * Assign a transaction to a statement cycle by **posted_date** (docs/04 §1).
 * Refunds keep `refunds_transaction_id` on the ledger row; this only sets cycle membership.
 */
export function assignCycle(input: {
  card_id: string;
  posted_date: string | null;
  cycles: readonly StatementCycleInput[];
  anchors: CycleAnchors | null;
  /** Defaults to low when generating; caller should pass resolveCycleAnchors confidence. */
  projection_confidence?: ProjectionConfidence;
  /** Required when a projected cycle must be generated. */
  new_cycle_id?: string;
}): AssignCycleResult {
  if (input.posted_date === null) {
    return {
      statement_cycle_id: null,
      assignment: 'unassigned',
      generated_cycle: null,
      projection_confidence: null,
    };
  }

  const posted = businessDate(input.posted_date);
  const found = findContainingCycle(input.card_id, posted, input.cycles);
  if (found !== null) {
    return {
      statement_cycle_id: found.id,
      assignment: found.is_projected ? 'estimated' : 'confirmed',
      generated_cycle: null,
      projection_confidence: found.is_projected ? (input.projection_confidence ?? 'low') : null,
    };
  }

  if (input.anchors === null) {
    return {
      statement_cycle_id: null,
      assignment: 'unassigned',
      generated_cycle: null,
      projection_confidence: null,
    };
  }

  const newId = input.new_cycle_id;
  if (newId === undefined || newId.trim().length === 0) {
    throw new Error('assignCycle requires new_cycle_id to generate a projected cycle');
  }

  const generated = buildProjectedCycle({
    id: newId,
    cardId: input.card_id,
    anchors: input.anchors,
    posted,
  });
  const confidence = input.projection_confidence ?? 'low';
  return {
    statement_cycle_id: generated.id,
    assignment: 'estimated',
    generated_cycle: generated,
    projection_confidence: confidence,
  };
}
